import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import config from '../config';
import {
  ADD_CHANNEL_MEMBERS,
  ARCHIVE_CONVERSATION,
  CREATE_AI_CONVERSATION,
  CREATE_CHANNEL,
  CREATE_DM,
  CREATE_GROUP_DM,
  DELETE_DIRECT_MESSAGE_FOR_ME,
  LIST_MY_CONVERSATIONS,
  MARK_CONVERSATION_READ,
  REMOVE_CHANNEL_MEMBER,
  RENAME_CONVERSATION,
  TOGGLE_CONVERSATION_AI,
  TOGGLE_FAVORITE,
  UPDATE_PRESENCE,
  ON_NEW_MESSAGE,
  GET_MY_PROFILE
} from '../graphql/operations';
import {
  DING_THROTTLE_MS,
  buildSelfIdentitySet,
  buildUnreadMap,
  isSelfMessageEvent,
  shouldPlayDing,
  syncUnreadCounts
} from './workspaceUnreadUtils';
import { beginDingAttempt } from './dingThrottle';
import {
  getNotificationSoundSrc,
  resolveNotificationSoundType
} from './notificationSoundCatalog';
import {
  prepareNotificationSoundElement,
  playNotificationSoundElement
} from './notificationSoundPlayback';

// How long after the last in-flight mark-read for a conversation settles to
// keep that conversation in the suppression buffer. Covers DynamoDB
// eventual-consistency on `membership.lastReadAt` reads — a concurrent
// LIST_MY_CONVERSATIONS that started before our write was visible can
// otherwise return stale unreadCount AFTER our refetch's promise resolves.
// Combined with the "keep previous local" semantics in `syncUnreadCounts`,
// real unreads bumped by the ON_NEW_MESSAGE subscription are preserved
// even while the buffer is active.
const RECENTLY_MARKED_POST_SETTLE_TTL_MS = 2000;

export const WorkspaceContext = createContext({
  conversations: [],
  channels: [],
  dms: [],
  aiConversations: [],
  favorites: [],
  recentChats: [],
  loading: false,
  error: null,
  selectedConversation: null,
  selectConversation: () => {},
  createConversation: () => {},
  createChannel: () => {},
  createDM: () => {},
  createGroupDM: () => {},
  createAIConversation: () => {},
  archiveConversation: () => {},
  renameConversation: () => {},
  toggleAI: () => {},
  toggleFavorite: () => {},
  markRead: () => {},
  addChannelMembers: () => {},
  removeChannelMember: () => {},
  updateMembers: () => {},
  aiToggleEnabled: false,
  unreadCounts: {},
  notificationSoundEnabled: true,
  notificationSoundType: 'classic',
  prepareNotificationSound: () => ({ prepared: false, reason: 'unsupported' }),
  testNotificationSound: async () => ({ played: false, reason: 'unsupported' }),
  refetch: () => {}
});

export function WorkspaceProvider({ children }) {
  const aiToggleEnabled = Boolean(config?.features?.conversationAiToggleEnabled);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const prevSelectedIdRef = useRef(null);
  const selectedConversationIdRef = useRef(null);
  const lastDingAtRef = useRef(0);
  const dingPlayInFlightRef = useRef(false);
  const subscriptionsByConversationRef = useRef(new Map());
  // Suppression buffer for mark-read reconciliation. Each entry tracks how
  // many mark-read mutations are currently in flight for that conversation
  // (refCount, for overlapping calls), plus an optional post-settle deadline
  // (expiresAt) and the timer scheduled to clean it up. We treat an id as
  // "in the buffer" iff refCount > 0 OR Date.now() < expiresAt; this keeps the
  // buffer active across the eventual-consistency window after the last
  // in-flight mutation resolves.
  // Shape: Map<conversationId, { refCount, expiresAt, timerId }>.
  const recentlyMarkedReadEntriesRef = useRef(new Map());
  // The `.has(id)` view passed to syncUnreadCounts.
  const recentlyMarkedReadIdsView = useRef({
    has: (conversationId) => {
      const entry = recentlyMarkedReadEntriesRef.current.get(conversationId);
      if (!entry) return false;
      if (entry.refCount > 0) return true;
      if (entry.expiresAt && Date.now() < entry.expiresAt) return true;
      return false;
    }
  }).current;
  const apolloClient = useApolloClient();

  // Cancel any pending TTL timers if the provider unmounts.
  useEffect(() => {
    const entries = recentlyMarkedReadEntriesRef.current;
    return () => {
      entries.forEach((entry) => {
        if (entry.timerId) clearTimeout(entry.timerId);
      });
      entries.clear();
    };
  }, []);

  // Fetch user profile for notification subscription
  const { data: profileData } = useQuery(GET_MY_PROFILE);
  const profileUserId = profileData?.getMyProfile?.userId || null;
  const profileUsername = profileData?.getMyProfile?.username || null;
  const profileEmail = profileData?.getMyProfile?.email || null;
  const profileDisplayName = profileData?.getMyProfile?.displayName || null;
  const notificationSoundEnabled = profileData?.getMyProfile?.notificationSoundEnabled !== false;
  const notificationSoundType = resolveNotificationSoundType(
    profileData?.getMyProfile?.notificationSoundType
  );

  // Stable refs for mention detection from inside the per-conversation
  // subscription callbacks below. The subscription closures are created
  // with stale identity unless we ref-bridge.
  const currentUserUsernameRef = useRef('');
  const currentUserDisplayNameRef = useRef('');
  useEffect(() => {
    currentUserUsernameRef.current = profileUsername || profileEmail || '';
    currentUserDisplayNameRef.current = profileDisplayName || '';
  }, [profileUsername, profileEmail, profileDisplayName]);

  // Primary conversation list query
  const { loading, error, data, refetch } = useQuery(LIST_MY_CONVERSATIONS, {
    fetchPolicy: 'network-only'
  });

  // Stable handle for the post-TTL refetch trigger so the setTimeout callback
  // doesn't capture a stale `refetch` and doesn't need to participate in the
  // mark-read useCallback's dependency list.
  const listRefetchRef = useRef(refetch);
  useEffect(() => {
    listRefetchRef.current = refetch;
  }, [refetch]);

  // Raw list from server
  const rawConversations = useMemo(() => data?.listMyConversations || [], [data]);

  useEffect(() => {
    selectedConversationIdRef.current = selectedConversation?.id || null;
  }, [selectedConversation?.id]);

  // Flatten conversations for backward compatibility: attach conversation-level fields
  const conversations = useMemo(
    () =>
      rawConversations.map((item) => ({
        ...item.conversation,
        aiEnabled: Boolean(item?.conversation?.aiEnabled),
        membership: item.membership,
        unreadCount: item.unreadCount || 0
      })),
    [rawConversations]
  );

  // Categorize conversations
  const channels = useMemo(
    () => conversations.filter((c) => c.type === 'CHANNEL' && !c.membership?.isFavorited),
    [conversations]
  );

  const dms = useMemo(
    () => conversations.filter((c) => c.type === 'DM' && !c.membership?.isFavorited),
    [conversations]
  );

  const aiConversations = useMemo(
    () => conversations.filter((c) => c.type === 'AI' && !c.membership?.isFavorited),
    [conversations]
  );

  const favorites = useMemo(
    () => conversations.filter((c) => c.membership?.isFavorited === true),
    [conversations]
  );

  const conversationTypeById = useMemo(() => {
    const map = {};
    conversations.forEach((conversation) => {
      if (!conversation?.id) return;
      map[conversation.id] = String(conversation.type || '').toUpperCase();
    });
    return map;
  }, [conversations]);

  const selfIdentitySet = useMemo(() => {
    const membershipUserIds = rawConversations
      .map((item) => item?.membership?.userId)
      .filter(Boolean);
    return buildSelfIdentitySet({
      profileUserId,
      profileUsername,
      profileEmail,
      membershipUserIds
    });
  }, [profileUserId, profileUsername, profileEmail, rawConversations]);

  // Backward compat: recentChats is the full list (used by Sidebar, NewChannelModal, NewDMModal)
  const recentChats = conversations;

  // Auto-select first conversation if none selected
  useEffect(() => {
    if (!selectedConversation && conversations.length > 0) {
      setSelectedConversation(conversations[0]);
    }
  }, [conversations, selectedConversation]);

  const selectConversation = useCallback((conversation) => {
    setSelectedConversation(conversation);
  }, []);

  // Two mark-read mutation hooks. Both populate the in-flight suppression
  // buffer; they differ only in whether they request an immediate
  // LIST_MY_CONVERSATIONS refetch:
  // - `markConversationReadWithRefetch` (used on conversation change): awaits
  //   a fresh list so the unread map reconciles against post-write server
  //   state.
  // - `markConversationReadBareMutation` (used per-message via
  //   ON_NEW_MESSAGE): no per-call refetch, to avoid a full list refetch on
  //   every incoming message in a busy channel. The buffer + post-settle TTL
  //   refetch handles eventual consistency once activity quiets.
  const [markConversationReadWithRefetch] = useMutation(MARK_CONVERSATION_READ, {
    refetchQueries: [{ query: LIST_MY_CONVERSATIONS }],
    awaitRefetchQueries: true
  });
  const [markConversationReadBareMutation] = useMutation(MARK_CONVERSATION_READ);
  const [updatePresenceHeartbeatMutation] = useMutation(UPDATE_PRESENCE);

  // Buffer-lifecycle helper. Returns a `settleBuffer` callback for the caller
  // to invoke when its mutation resolves. Adding the conversation to the
  // buffer on entry blocks stale list responses (a concurrent
  // LIST_MY_CONVERSATIONS that lands while we're still in the eventual-
  // consistency window) from restoring an unread count we've just cleared.
  // On `settleBuffer`, refCount drops; when it reaches zero the entry is held
  // for a short post-settle TTL and then a fresh LIST_MY_CONVERSATIONS refetch
  // is triggered so any real unread we might have suppressed is resynced.
  const beginInFlightMarkRead = useCallback((conversationId) => {
    if (!conversationId) return () => {};
    const entries = recentlyMarkedReadEntriesRef.current;
    let entry = entries.get(conversationId);
    if (!entry) {
      entry = { refCount: 0, expiresAt: 0, timerId: null };
      entries.set(conversationId, entry);
    }
    if (entry.timerId) {
      clearTimeout(entry.timerId);
      entry.timerId = null;
    }
    entry.expiresAt = 0;
    entry.refCount += 1;

    return () => {
      const current = entries.get(conversationId);
      if (!current) return;
      current.refCount = Math.max(0, current.refCount - 1);
      if (current.refCount !== 0) return;
      current.expiresAt = Date.now() + RECENTLY_MARKED_POST_SETTLE_TTL_MS;
      current.timerId = setTimeout(() => {
        const stillCurrent = entries.get(conversationId);
        if (stillCurrent !== current || current.refCount !== 0) return;
        entries.delete(conversationId);
        listRefetchRef.current?.().catch((err) => {
          // eslint-disable-next-line no-console
          console.error('post-mark-read LIST_MY_CONVERSATIONS refetch failed', err);
        });
      }, RECENTLY_MARKED_POST_SETTLE_TTL_MS);
    };
  }, []);

  const markConversationReadOnSwitch = useCallback(
    (conversationId) => {
      if (!conversationId) return Promise.resolve();
      const settleBuffer = beginInFlightMarkRead(conversationId);

      const fire = () =>
        markConversationReadWithRefetch({ variables: { conversationId } });

      const settle = fire().catch((firstError) => {
        // eslint-disable-next-line no-console
        console.error('markConversationRead failed; retrying once', firstError);
        return new Promise((resolve) => {
          setTimeout(() => {
            fire()
              .then(resolve)
              .catch((retryError) => {
                // eslint-disable-next-line no-console
                console.error('markConversationRead retry failed', retryError);
                resolve();
              });
          }, 1000);
        });
      });

      return settle.finally(settleBuffer);
    },
    [beginInFlightMarkRead, markConversationReadWithRefetch]
  );

  useEffect(() => {
    const currentId = selectedConversation?.id;
    if (!currentId || currentId === prevSelectedIdRef.current) return;
    prevSelectedIdRef.current = currentId;

    // Reset local unread count immediately
    setUnreadCounts((prev) => ({ ...prev, [currentId]: 0 }));

    markConversationReadOnSwitch(currentId);
  }, [selectedConversation, markConversationReadOnSwitch]);

  const prepareNotificationSound = useCallback(({ soundType } = {}) => {
    const resolvedSoundType = resolveNotificationSoundType(soundType || notificationSoundType);
    const src = getNotificationSoundSrc(resolvedSoundType);
    return prepareNotificationSoundElement({
      src,
      enabled: notificationSoundEnabled,
      volume: 1
    });
  }, [notificationSoundEnabled, notificationSoundType]);

  useEffect(() => {
    prepareNotificationSound({ soundType: notificationSoundType });
  }, [notificationSoundType, prepareNotificationSound]);

  const playDing = useCallback(async ({ bypassThrottle = false, soundType } = {}) => {
    if (typeof window === 'undefined') return { played: false, reason: 'unsupported' };
    if (dingPlayInFlightRef.current) return { played: false, reason: 'in_flight' };

    const now = Date.now();
    const attempt = beginDingAttempt({
      now,
      lastDingAt: lastDingAtRef.current,
      bypassThrottle,
      throttleMs: DING_THROTTLE_MS
    });
    if (!attempt.allowed) {
      return { played: false, reason: 'throttled' };
    }
    if (!bypassThrottle) {
      lastDingAtRef.current = attempt.nextLastDingAt;
    }

    dingPlayInFlightRef.current = true;
    try {
      const resolvedSoundType = resolveNotificationSoundType(soundType || notificationSoundType);
      const src = getNotificationSoundSrc(resolvedSoundType);
      const result = await playNotificationSoundElement({
        src,
        enabled: notificationSoundEnabled,
        volume: 1
      });
      return result;
    } catch (_error) {
      return { played: false, reason: 'blocked' };
    } finally {
      dingPlayInFlightRef.current = false;
    }
  }, [
    notificationSoundEnabled,
    notificationSoundType
  ]);

  const testNotificationSound = useCallback(({ soundType } = {}) => {
    return playDing({ bypassThrottle: true, soundType });
  }, [playDing]);

  // Keep unread counts in sync with server truth on each conversation query refresh.
  useEffect(() => {
    const incomingUnreadMap = buildUnreadMap(rawConversations);
    setUnreadCounts((prev) => {
      return syncUnreadCounts(
        prev,
        incomingUnreadMap,
        selectedConversation?.id || null,
        recentlyMarkedReadIdsView
      );
    });
  }, [
    rawConversations,
    recentlyMarkedReadIdsView,
    selectedConversation?.id
  ]);

  // Tab title + favicon dot — flips the document title to "(N) Kapsa AI"
  // and swaps the favicon to a "has unread" variant whenever any
  // conversation has unread messages. Cheap, frontend-only, and resolves
  // Yvonne's "I work across many tabs and the pings are off" feedback
  // without depending on the Browser Notification API (which is permission-
  // gated and unavailable on some iOS Safari builds). The Notification API
  // and audio cues run alongside this in the subscription handler.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const totalUnread = Object.values(unreadCounts || {}).reduce(
      (sum, n) => sum + (Number(n) || 0),
      0
    );
    const baseTitle = 'Kapsa AI';
    document.title = totalUnread > 0 ? `(${totalUnread}) ${baseTitle}` : baseTitle;

    // Favicon swap. We keep a single <link rel="icon"> element across
    // both states; the public/ folder ships favicon.png (default) and
    // favicon-unread.png (with a red dot). Falls back gracefully to the
    // default if the unread variant isn't deployed yet.
    const ensureLink = () => {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'icon');
        document.head.appendChild(link);
      }
      return link;
    };
    const link = ensureLink();
    // public/icon.svg is the deployed default (see index.html). The unread
    // variant lives at /icon-unread.svg and adds a red dot in the corner.
    // We stash the original href on link.dataset.defaultHref so we can
    // restore it after unread drops to zero, regardless of cache headers.
    const desired = totalUnread > 0
      ? '/icon-unread.svg'
      : (link.dataset.defaultHref || link.getAttribute('href') || '/icon.svg');
    if (!link.dataset.defaultHref && totalUnread === 0) {
      link.dataset.defaultHref = link.getAttribute('href') || '/icon.svg';
    }
    if (link.getAttribute('href') !== desired) {
      link.setAttribute('href', desired);
    }
    return undefined;
  }, [unreadCounts]);

  // Defer the Browser Notification permission prompt until the user has
  // shown actual intent to use the app (first time we get a profile +
  // an unread bump). Calling Notification.requestPermission() on mount
  // is a known anti-pattern that triggers a modal before context is
  // established. We only prompt once per session.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof window.Notification !== 'function') return;
    if (window.Notification.permission !== 'default') return;
    // Don't prompt on first paint — wait for any user-visible activity.
    const totalUnread = Object.values(unreadCounts || {}).reduce(
      (sum, n) => sum + (Number(n) || 0),
      0
    );
    if (totalUnread === 0) return;
    try {
      window.Notification.requestPermission().catch(() => { /* user dismissed */ });
    } catch (_e) {
      // older browsers expect the legacy callback signature — non-fatal.
    }
  }, [unreadCounts]);

  useEffect(() => {
    if (!apolloClient) return undefined;

    const activeIds = new Set(
      conversations
        .map((conversation) => conversation?.id)
        .filter(Boolean)
    );
    const subscriptionsMap = subscriptionsByConversationRef.current;

    subscriptionsMap.forEach((subscription, conversationId) => {
      if (!activeIds.has(conversationId)) {
        subscription.unsubscribe?.();
        subscriptionsMap.delete(conversationId);
      }
    });

    activeIds.forEach((conversationId) => {
      if (subscriptionsMap.has(conversationId)) return;

      const subscription = apolloClient
        .subscribe({
          query: ON_NEW_MESSAGE,
          variables: { conversationId }
        })
        .subscribe({
          next: ({ data: payload }) => {
            const message = payload?.onNewMessage;
            const convId = message?.conversationId;
            if (!convId) return;
            if (isSelfMessageEvent(message, selfIdentitySet)) return;

            const selectedConversationId = selectedConversationIdRef.current;
            if (convId === selectedConversationId) return;

            // @mention boost: if the message content contains a mention
            // of the current user, play a more attention-grabbing ding
            // (different preset), bypass the "selected conversation"
            // suppression, and fire a higher-priority OS notification.
            // Detection is heuristic — we match either an @-prefixed
            // version of the user's username/displayName or the mention
            // marker syntax produced by MentionPill (`@[name]`).
            const myUsername = currentUserUsernameRef.current || '';
            const myDisplayName = currentUserDisplayNameRef.current || '';
            const contentText = String(message?.content || '');
            const mentionPatterns = [myUsername, myDisplayName]
              .filter(Boolean)
              .flatMap((name) => [
                `@${name}`,
                `@[${name}]`
              ]);
            const isMention = mentionPatterns.some((p) => p && contentText.toLowerCase().includes(p.toLowerCase()));

            // Fire an OS notification when the tab is hidden, OR always
            // for mentions. Tag-based dedupe ensures only one
            // notification per conversation is visible — typical Slack
            // behavior keeps the UI quiet during a flood.
            try {
              if (
                typeof window !== 'undefined' &&
                typeof window.Notification === 'function' &&
                window.Notification.permission === 'granted' &&
                (document.visibilityState === 'hidden' || isMention)
              ) {
                const senderLabel = message?.senderDisplayName || message?.senderId || 'New message';
                const previewBody = contentText.length > 140
                  ? `${contentText.slice(0, 140)}…`
                  : contentText;
                // requireInteraction keeps mention notifications visible
                // until dismissed — important for the cross-tab use case
                // where Yvonne is working through many tabs and doesn't
                // want to miss a directed ping.
                //
                // The constructor return value is intentionally unused —
                // the OS owns the lifecycle and the side-effect (rendering
                // the notification) is what we want.
                // eslint-disable-next-line no-new
                new window.Notification(senderLabel, {
                  body: previewBody || 'New message',
                  tag: convId,
                  requireInteraction: !!isMention,
                  silent: false
                });
                // No click handler wiring here — clicking the OS
                // notification just focuses the window. Selection by id
                // can be added later when the deep-link surface lands.
              }
            } catch (_e) { /* permission flips between checks etc. — non-fatal */ }

            setUnreadCounts((prev) => {
              const previousUnread = Number(prev?.[convId] || 0);
              const nextUnread = previousUnread + 1;
              const shouldDing = isMention || shouldPlayDing({
                notificationSoundEnabled,
                conversationType: conversationTypeById[convId],
                conversationId: convId,
                selectedConversationId,
                previousUnread,
                nextUnread,
                now: Date.now(),
                lastDingAt: lastDingAtRef.current
              });
              if (shouldDing) {
                // Mention boost uses a distinct sound preset so users
                // can tell a directed ping from background chatter.
                playDing(isMention ? { soundType: 'mention', bypassThrottle: true } : undefined).catch(() => {});
              }
              return { ...prev, [convId]: nextUnread };
            });
          },
          error: (err) => {
            // Polling reconciles unread counts via LIST_MY_CONVERSATIONS,
            // but a silent no-op here masks subscription transport failures
            // (e.g., the MQTT→pure-WS migration to the in-tree AppSyncWsLink).
            // Log so regressions are visible instead of silently falling back.
            // eslint-disable-next-line no-console
            console.warn('[appsync-subscription] unread subscription error', err);
          }
        });

      subscriptionsMap.set(conversationId, subscription);
    });

    return () => {
      subscriptionsMap.forEach((subscription) => subscription.unsubscribe?.());
      subscriptionsMap.clear();
    };
  }, [
    apolloClient,
    conversationTypeById,
    conversations,
    notificationSoundEnabled,
    playDing,
    selfIdentitySet
  ]);

  useEffect(() => {
    if (selfIdentitySet.size === 0 || typeof document === 'undefined') return undefined;

    const sendHeartbeat = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      updatePresenceHeartbeatMutation({ variables: {} }).catch(() => {
        // Presence is best-effort. The UI keeps rendering the last known state.
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        sendHeartbeat();
      }
    };

    const intervalId = setInterval(sendHeartbeat, 60000);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updatePresenceHeartbeatMutation, selfIdentitySet]);

  // ── Mutations ───────────────────────────────────────────────────────────────

  const [createChannelMutation] = useMutation(CREATE_CHANNEL, {
    onCompleted: (result) => {
      const created = result?.createChannel;
      if (created) {
        setSelectedConversation({ ...created, membership: null, unreadCount: 0 });
      }
      refetch();
    }
  });

  const [createDMMutation] = useMutation(CREATE_DM, {
    onCompleted: (result) => {
      const created = result?.createDM;
      if (created) {
        setSelectedConversation({ ...created, membership: null, unreadCount: 0 });
      }
      refetch();
    }
  });

  const [createGroupDMMutation] = useMutation(CREATE_GROUP_DM, {
    onCompleted: (result) => {
      const created = result?.createGroupDM;
      if (created) {
        setSelectedConversation({ ...created, membership: null, unreadCount: 0 });
      }
      refetch();
    }
  });

  const [createAIConversationMutation] = useMutation(CREATE_AI_CONVERSATION, {
    onCompleted: (result) => {
      const created = result?.createAIConversation;
      if (created) {
        setSelectedConversation({ ...created, membership: null, unreadCount: 0 });
      }
      refetch();
    }
  });

  const [archiveConversationMutation] = useMutation(ARCHIVE_CONVERSATION, {
    onCompleted: (_data, clientOptions) => {
      const archivedId = clientOptions?.variables?.id;
      if (selectedConversation?.id === archivedId) {
        setSelectedConversation(null);
      }
      refetch();
    }
  });

  const [renameConversationMutation] = useMutation(RENAME_CONVERSATION);
  const [toggleAIMutation] = useMutation(TOGGLE_CONVERSATION_AI);

  const [toggleFavoriteMutation] = useMutation(TOGGLE_FAVORITE, {
    onCompleted: () => refetch()
  });

  const [addChannelMembersMutation] = useMutation(ADD_CHANNEL_MEMBERS, {
    onCompleted: () => refetch()
  });

  const [removeChannelMemberMutation] = useMutation(REMOVE_CHANNEL_MEMBER, {
    onCompleted: () => refetch()
  });

  const [deleteDmForMeMutation] = useMutation(DELETE_DIRECT_MESSAGE_FOR_ME, {
    onCompleted: () => refetch()
  });

  // ── Wrapped handlers ─────────────────────────────────────────────────────────

  const createChannel = useCallback(
    (title, description, memberIds = []) =>
      createChannelMutation({ variables: { title, description, memberIds } }),
    [createChannelMutation]
  );

  const createDM = useCallback(
    (recipientId) => createDMMutation({ variables: { recipientId } }),
    [createDMMutation]
  );

  const createGroupDM = useCallback(
    (recipientIds) => createGroupDMMutation({ variables: { input: { recipientIds } } }),
    [createGroupDMMutation]
  );

  const createAIConversation = useCallback(
    (title) => createAIConversationMutation({ variables: { title } }),
    [createAIConversationMutation]
  );

  // Backward-compatible createConversation wrapper used by existing modals
  // Signature: createConversation(title, type, members)
  const createConversation = useCallback(
    (title, type = 'DM', members = []) => {
      if (type === 'CHANNEL') {
        return createChannel(title, null, members);
      }
      if (type === 'AI') {
        return createAIConversation(title);
      }
      if (members.length > 1) {
        return createGroupDM(members);
      }
      const recipientId = members[0] || title;
      return createDM(recipientId);
    },
    [createChannel, createAIConversation, createDM, createGroupDM]
  );

  const archiveConversation = useCallback(
    (id) => archiveConversationMutation({ variables: { id } }),
    [archiveConversationMutation]
  );

  const renameConversation = useCallback(
    async (conversationId, newTitle) => {
      const result = await renameConversationMutation({
        variables: { id: conversationId, title: newTitle }
      });
      const renamed = result?.data?.renameConversation;
      if (renamed && selectedConversation?.id === conversationId) {
        setSelectedConversation((current) =>
          current?.id === conversationId ? { ...current, ...renamed } : current
        );
      }
      await refetch();
    },
    [renameConversationMutation, selectedConversation, refetch]
  );

  const toggleAI = useCallback(
    async (conversationId) => {
      if (!aiToggleEnabled) {
        return null;
      }
      try {
        const result = await toggleAIMutation({ variables: { conversationId } });
        const updated = result?.data?.toggleConversationAI;
        if (updated && selectedConversation?.id === conversationId) {
          setSelectedConversation((current) =>
            current?.id === conversationId ? { ...current, aiEnabled: updated.aiEnabled } : current
          );
        }
        await refetch();
      } catch (err) {
        console.error('Failed to toggle AI:', err);
        await refetch();
      }
    },
    [aiToggleEnabled, toggleAIMutation, selectedConversation, refetch]
  );

  const toggleFavorite = useCallback(
    (conversationId) => toggleFavoriteMutation({ variables: { conversationId } }),
    [toggleFavoriteMutation]
  );

  // Public mark-read used by ON_NEW_MESSAGE while the conversation is the
  // active selection. Bare mutation (no per-call list refetch, to avoid a
  // refetch storm in busy channels), but it DOES populate the in-flight
  // buffer so that if the user immediately switches away, the next
  // conversation switch's LIST_MY_CONVERSATIONS refetch cannot accept a
  // stale unread for the conversation we just left. The buffer's
  // post-settle TTL refetch (~2 s after the last bare mutation settles)
  // ensures the canonical state is eventually reconciled.
  const markRead = useCallback(
    (conversationId) => {
      if (!conversationId) return Promise.resolve();
      setUnreadCounts((prev) => ({ ...prev, [conversationId]: 0 }));
      const settleBuffer = beginInFlightMarkRead(conversationId);
      return markConversationReadBareMutation({ variables: { conversationId } })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('markConversationRead (bare) failed', err);
        })
        .finally(settleBuffer);
    },
    [beginInFlightMarkRead, markConversationReadBareMutation]
  );

  const addChannelMembers = useCallback(
    // includeHistory is REQUIRED at the GraphQL surface. We default to true
    // here so existing call sites (which haven't been updated) preserve the
    // pre-PR Slack-style behavior of full-history visibility. ManageMembers
    // and other modal flows that surface a UI choice will pass an explicit
    // boolean.
    (conversationId, userIds, includeHistory = true) =>
      addChannelMembersMutation({
        variables: { conversationId, userIds, includeHistory: !!includeHistory }
      }),
    [addChannelMembersMutation]
  );

  const removeChannelMember = useCallback(
    (conversationId, memberUserId) =>
      removeChannelMemberMutation({ variables: { conversationId, userId: memberUserId } }),
    [removeChannelMemberMutation]
  );

  // Per-user soft hide for DMs. Sets historyStartAt + hiddenInSidebar on
  // the caller's own MEMBER row. Backend derives the userId from the
  // authenticated identity — we never pass one. UI strings should say
  // "Hide chat", not "Delete chat" (no GDPR erasure; peer is unaffected).
  const hideDirectMessage = useCallback(
    (conversationId) =>
      deleteDmForMeMutation({ variables: { conversationId } }),
    [deleteDmForMeMutation]
  );

  // Backward-compatible updateMembers wrapper (used by ManageMembersModal)
  const updateMembers = useCallback(
    async (conversationId, memberList) => {
      // Determine adds/removes by comparing with current conversation members
      const conv = conversations.find((c) => c.id === conversationId);
      const currentMembers = conv?.members || [];
      const toAdd = memberList.filter((m) => !currentMembers.includes(m));
      const toRemove = currentMembers.filter((m) => !memberList.includes(m));

      const promises = [];
      if (toAdd.length > 0) {
        promises.push(addChannelMembers(conversationId, toAdd));
      }
      for (const uid of toRemove) {
        promises.push(removeChannelMember(conversationId, uid));
      }
      await Promise.all(promises);
    },
    [conversations, addChannelMembers, removeChannelMember]
  );

  // ── Context value ─────────────────────────────────────────────────────────────

  const value = useMemo(
    () => ({
      conversations,
      channels,
      dms,
      aiConversations,
      favorites,
      recentChats,
      loading,
      error,
      selectedConversation,
      selectConversation,
      createConversation,
      createChannel,
      createDM,
      createGroupDM,
      createAIConversation,
      archiveConversation,
      renameConversation,
      toggleAI,
      toggleFavorite,
      markRead,
      addChannelMembers,
      removeChannelMember,
      hideDirectMessage,
      updateMembers,
      aiToggleEnabled,
      unreadCounts,
      notificationSoundEnabled,
      notificationSoundType,
      prepareNotificationSound,
      testNotificationSound,
      refetch
    }),
    [
      conversations,
      channels,
      dms,
      aiConversations,
      favorites,
      recentChats,
      loading,
      error,
      selectedConversation,
      selectConversation,
      createConversation,
      createChannel,
      createDM,
      createGroupDM,
      createAIConversation,
      archiveConversation,
      renameConversation,
      toggleAI,
      toggleFavorite,
      markRead,
      addChannelMembers,
      removeChannelMember,
      hideDirectMessage,
      updateMembers,
      aiToggleEnabled,
      unreadCounts,
      notificationSoundEnabled,
      notificationSoundType,
      prepareNotificationSound,
      testNotificationSound,
      refetch
    ]
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}
